export interface Guide {
  slug: string;
  title: string;
  tags: string[];
  body: string;
}

const GETTING_STARTED_BODY = `# Getting Started

LocalAgent runs your AI agents against the model providers you configure. Getting a
working setup takes three steps: configure a provider, pick (or add) a model, and
create a persona that uses it.

## 1. Configure a provider

Open **Agents → Providers**. Each provider row shows how many models it exposes and
whether credentials are configured. Cloud providers (Anthropic, OpenAI, Groq,
Fireworks, and others) need an API key: if your platform operator has not configured
one, an account admin can add an account-scoped key in the **Provider Key** section of
the provider's detail panel. Keys are validated against the live provider before they
are stored, and they are encrypted at rest. LM Studio is the local exception — it uses
your desktop device's running LM Studio instance and needs no key.

## 2. Pick or add a model

Every provider carries a model catalog with two tiers. **Global** entries are read-only
mirrors of the platform's seeded catalog — use **Sync Global** to refresh them. **Account**
entries belong to your account: create one with **+ Add Model**, copy a global entry to
customize it, or use **Discover…** to fetch the provider's live model list and import
models directly. An entry's *name* is how personas reference it; its *model id* is what
the provider actually serves — the two are independent, so you can rename without
breaking anything.

## 3. Create a persona

Open **Agents → Agents** and create a new agent (persona). Give it a name, a system
prompt, and select the provider + model entry it should use. Optionally attach an
agent process — the reasoning structure the persona runs each turn (see Key Concepts).
Seeded default personas ship with sensible flagship processes already attached, so the
fastest path is to start from one of those and edit it.

## 4. Chat

Head to **Chat**, pick your persona, and send a message. On desktop you can attach a
workspace so the agent can read and edit files; on the web, chats run workspace-less.
If a turn fails with a provider error, check the provider's key configuration and the
model entry's context-window setting under **Agents → Providers**.
`;

const KEY_CONCEPTS_BODY = `# Key Concepts

## Personas

A persona is a configured agent: a name, a system prompt, a provider + model entry,
and optional defaults such as an attached agent process. Personas are account-scoped —
read-only **Templates** come seeded with the platform, and anything you create or copy
lives in your account and is fully editable. When you start a chat you choose the
persona that drives it; the persona decides which model answers and how it behaves.

## Agent processes

An agent process is the reasoning structure a session runs each turn — a validated
graph of steps ending in a single respond terminal. The seeded catalog ranges from
**Simple** (one pass, answer directly) to goal-driven processes that plan and execute,
then have their result checked: a goal-checking process records each request as a
session task and turns Keep working on, and when the agent settles a task check judges
the work against the task list and sends it back with what is missing until every task
is done. Every process runs one pass per turn. Steps can name their own persona (a
researcher step, an auditor step) or inherit the session's persona. Every session
resolves a process when it is created: an explicit choice wins, then the persona's
default, then Simple. Copy a system process into your account to customize it.

A persona can instead enable **automatic process selection** and assign the processes
it permits. For each new message, a dedicated internal agent considers the current
conversation plus those processes' descriptions and steps, then records the selected
process and reason in the turn details. A chat can inherit the persona setting or hold
an explicit override. The configured manual process is retained while automatic mode
is on and returns unchanged when automatic mode is disabled.

Internal planning, evaluation, selection, and compaction agents use the same
process-controlled execution system. Each chat's **Internal agents** setting controls
how their progress is shown: **Friendly** uses product-facing role labels, while
**Exact** also shows the system persona and process provenance. This is a display
choice for that chat only and does not change how any agent runs.

## Workspaces

A workspace connects an agent to a filesystem — a repository the agent can read,
search, and edit with real tools. Workspaces are a desktop capability: the desktop app
registers local folders, and cloud workspaces provision an isolated pod around a cloud
repository. Web chats always run workspace-less. Cloud workspaces hibernate when idle
and resume on demand, and their \`.shogunchop/\` directory carries steering rules and
skills the agent reads fresh every turn.

## Context sources

Context sources feed extra knowledge into a session beyond the chat history —
documents, integration data, and other account content the agent can draw on. The
session's context store is the durable system of record: every message and tool result
is persisted server-side, and long conversations are compacted by summarization rather
than truncation, so agents keep working memory without losing the verbatim recent
window. Search across your account's indexed content is available to agents as a tool
and to you on the **Search** page.
`;

const TEST_SCRIPTS_BODY = `# Test Scripts

A test script makes an agent run do exactly what you wrote, every time. You put the
script in the message you send; an agent running on the **test provider** follows it
step by step, and the platform does everything that follows for real: tools execute,
sub-agents start, files change, events stream. Use scripts to test workflows you build
on the platform without paying for, or depending on, a real model's judgment.

The script is the prompt. A real model reading the same message sees plain
instructions ("say this", "call this tool with these arguments") and can follow them
too, but only the test provider follows them exactly.

## How a scripted run works

- **The test provider plays the model.** Each time the agent asks its model for the next
  response, the test provider emits the next step of the script: a reply, one or more
  tool calls, or a failure. The platform then acts on that response exactly as it would
  on a real model's.
- **One step per model call.** A turn that calls a tool and then replies takes two steps:
  the call, and the reply that follows its result.
- **Results are real.** A tool call executes against the real tool, and a later step can
  use its result through a reference (\`{{turn[0].result.id}}\`).
- **Children carry their own scripts.** When a step hands work to another agent (a
  delegate, a process step, a work item), a \`hand_off\` puts that agent's script into the
  text it receives. Each agent reads only its own script.
- **No script, no surprises.** An agent whose message carries no script replies with a
  fixed default reply. A script with a mistake in it fails the turn with an error naming
  the mistake; it is never half-followed.

## Route an agent to the test provider

Create a persona whose model is the test provider: provider **test**, model
**test/scripted**. Chats with that persona follow scripts. Through the SDK:

\`\`\`text
provider_type: "test"
model_name:    "test/scripted"
\`\`\`

Platform helpers (title generation, summaries, process selection, evaluation, image
reading) run on the models assigned to your account's model roles. To script those too,
a platform admin points the account's roles at \`test\` / \`test/scripted\` with a
model-role override. The image reading role (\`vision_default\`) takes an override like
the others; \`test/scripted\` is marked vision capable, so images reach it for real.

## Example: a single scripted reply

Send this message to a test-provider persona. The agent replies "Hello from the
script." and the turn completes.

\`\`\`shogunchop-test/v2
turn:
  - say: Hello from the script.
\`\`\`

## The language

### Envelope and formats

A script is a fenced block opened by \`\`\`\` \`\`\`shogunchop-test/v2 \`\`\`\` at the start of a
line and closed by \`\`\`\` \`\`\` \`\`\`\`. It can sit anywhere in a message, after your own prose.
If a message holds several, the last one wins.

The body is one document written in **YAML or JSON**; both mean the same thing. The
reader is strict so that every idea has exactly one spelling. It rejects:

- duplicate keys,
- YAML anchors, aliases and merge keys (\`&\`, \`*\`, \`<<\`),
- explicit YAML tags,
- non-string keys,
- unknown fields, at any level,
- more than one document.

A body over 64 KiB is refused. In YAML, quote any value that starts with \`{\` or holds
\`[\` inside a flow (\`{…}\`) map, such as a reference: \`say: "id {{turn[0].result.id}}"\`.

### Document fields

| Field | Meaning |
|-------|---------|
| \`instructions\` | Plain-language preamble for a real model. The test provider ignores it. The SDK builder writes a default; pass an empty string to leave it out where space is tight, such as a feed post (280 characters). |
| \`turn\` | The steps this agent takes, one per model call, in order. |
| \`title\` | The session title, when the session's first exchange is titled. |
| \`decisions\` | Scripts for the platform's own decision agents, keyed by decision name (see **Decision names**). |
| \`scripts\` | Named scripts (root only), usable as a \`hand_off\` target anywhere below. |

A document needs a \`turn\` or \`decisions\`. A script with only \`decisions\` gives its own agent the default reply.

### Steps

Each step does exactly one thing: \`say\`, \`call\` or \`fail\`. \`think\`, \`pace\` and
\`expect\` modify it.

| Element | Shape | What happens |
|---------|-------|--------------|
| \`say\` | text | The agent replies with this text. References inside it are filled in. |
| \`call\` | \`{tool, with}\`, or a list of them | The agent calls these tools in one response, with these arguments. Built-in and MCP tools alike. A tool the agent isn't offered fails as a real model's call would. |
| \`fail\` | \`{kind, message}\` | The model call fails. \`transient\` fails one attempt and the retry continues with the next step; \`terminal\` fails the turn. |
| \`think\` | text | Reasoning text, streamed and recorded as a reasoning model's would be. |
| \`pace\` | \`{first_content_ms, chunk_gap_ms}\` | Waits before the first streamed chunk and between chunks (each up to 600000 ms). Use it to observe a call while it runs, or to simulate a stall. |
| \`expect\` | a list of checks | Checked before the step runs, against results already in the conversation. A check that doesn't hold fails the turn. |

There are no loops, conditions or variables. A script is a straight line: \`expect\`
guards a result, it never chooses a different next step.

### References

| You write | It becomes |
|-----------|------------|
| a \`with\` value that is exactly \`{{turn[N].result.path}}\` | The value at \`path\` in the real result of step N's tool call, keeping its type (a number stays a number). |
| \`{{turn[N].call[M].result.path}}\` | The same, for the M-th call of a step that made several. |
| \`{{…}}\` inside longer text | The value as text. |
| \`{{outer.turn[N].result.path}}\` | Inside a \`hand_off\`: the result of step N of the agent that handed the work over. Filled in when the hand-off happens. |
| \`{{this.field}}\` | A field of the work item the reading agent was handed (see **this**). Filled in by the platform when it hands the work over. |

\`path\` walks JSON keys and list indices joined by dots (\`hits.0.url\`). Large results
that arrive as a \`result://\` handle expose \`handle\`, \`task_id\` and \`message_index\`.
A reference may only name an earlier step; anything else is an error before the
script runs.

### Expectations

Each check names a result path and one matcher:

| Matcher | Holds when |
|---------|------------|
| \`equals\` | the value equals the given value |
| \`contains\` | the text contains the given text, or the list contains the given item |
| \`matches\` | the text matches the given regular expression (RE2) |
| \`present: true\` | the path exists and isn't null |

A failed check fails the turn with an error naming the step, the path, the matcher, the
expected value and the actual one. To check the last tool result of a turn, put the
\`expect\` on the closing \`say\`.

## Example: a tool call guarded by expect

The agent reads a file, checks what it got, and replies with part of it.

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: file_read
      with:
        path: README.md
  - say: "The README starts: {{turn[0].result.content}}"
    expect:
      - that: turn[0].result.content
        contains: "#"
\`\`\`

The same script in JSON:

\`\`\`shogunchop-test/v2
{"turn":[{"call":{"tool":"file_read","with":{"path":"README.md"}}},{"say":"The README starts: {{turn[0].result.content}}","expect":[{"that":"turn[0].result.content","contains":"#"}]}]}
\`\`\`

## Example: a call to your MCP tool

A tool from an MCP server you registered is called like any other. Its name is the
server's id, an underscore, and the tool's name, exactly as it appears in the agent's
tool list. Here the server's id is \`4f6c2a10\` and its tool is \`echo\`.

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: 4f6c2a10_echo
      with:
        message: ping from the script
  - say: "The server answered: {{turn[0].result}}"
\`\`\`

## Scripts for a whole tree

### hand_off

A \`hand_off\` is a value inside a \`call\`'s \`with\`. When the step runs, it becomes text:
the \`brief\`, a blank line, and the child's script in its own envelope. The delegate
goal, explore topic, process-step prompt or work item description therefore carries
the child's script, and the child agent follows it.

\`\`\`text
hand_off:
  brief: <optional prose for the receiving agent>
  script: <an inline script, or the name of a root \`scripts\` entry>
\`\`\`

### Named scripts

Define scripts once at the root under \`scripts\` and hand them off by name. Each
rendered child carries exactly the named scripts it will hand off further, and
nothing else. An unknown name, a name nothing hands off, or two scripts handing off to
each other in a cycle is an error.

### outer

A child's script is written before the child exists, so it can't contain ids the
parent hasn't created yet. \`{{outer.turn[N].result.path}}\` reaches the result of an
earlier step of the agent doing the hand-off, and is written into the child's script as
a plain value when the hand-off happens. It reaches one level up only; to pass a value
further down, the middle agent passes it on with its own \`outer\` reference. An \`outer\`
reference outside a \`hand_off\`, or to the hand-off step itself or a later one, is an
error.

### this

\`{{this.field}}\` names the work item the reading agent was handed, so a script written
before that item existed can still use its ids. The platform fills it in from the work
item itself when it builds the prompt:

| The agent reads | \`this\` is | Fields |
|-----------------|-----------|--------|
| a goal it decomposes | the goal | \`id\`, \`team_id\`, \`manager_position_id\` |
| a project it plans, or the manager's review or advance of it | the project | \`id\`, \`team_id\`, \`initiative_id\`, \`goal_id\`, and \`assignee_position_id\` once assigned |
| a ticket it works | the ticket | \`id\`, \`project_id\`, \`team_id\` |
| a work item it announces on the Live Feed | the item | \`id\` |
| a question another agent asked it (its \`question\` inbox item) | the question | \`id\` |

Any other field, or \`this\` where no work item was handed over, is an error naming the
field and the place.

## Example: a delegate with a child script

The root agent delegates one goal and waits for it (\`background: false\`); the child
lists the account's providers and reports the first; the root replies with what its
delegate reported. \`delegate\` needs an agent process that offers it (any process whose
respond step keeps the cognitive tools).

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: delegate
      with:
        background: false
        goals:
          - goal:
              hand_off:
                brief: List this account's providers and report the first one.
                script:
                  turn:
                    - call:
                        tool: provider_list
                    - say: "First provider: {{turn[0].result.providers.0.provider_type}}"
  - say: "My delegate reported: {{turn[0].result}}"
\`\`\`

## Scripts for the turn a delivery wakes

Work started in the background (a delegated goal, an explore topic, a plan, or any
tool called with \`background: true\`) delivers its result to the caller's inbox later,
and that delivery starts the caller's next turn when it is idle. That wake turn has
no user message, so its script travels inside the delivered result: end the child's
reply with a script envelope, and the wake turn follows it. The delivery is the one
place a quoted script stays live; everywhere else quoted scripts are neutralized.

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: delegate
      with:
        goals:
          - goal:
              hand_off:
                brief: Report in when done.
                script:
                  turn:
                    - say: |
                        Done.
                        \`\`\`shogunchop-test/v2
                        turn:
                          - say: The subagent reported in.
                        \`\`\`
  - say: Started one subagent; its result will arrive in my inbox.
\`\`\`

The SDK's \`testScript\` builds the inner envelope for you: put
\`testScript(wakeDocument)\` at the end of the child's \`say\` text.

## Scripts for questions between agents

A question asked with \`ask\` reaches an agent addressee as an inbox item, and an
answer reaches an asker that is not waiting as an inbox item; each starts a wake turn
with no user message. The question text carries the addressee's script, and the
answer text carries the asker's: give \`ask\`'s \`question\` or \`answer\`'s \`text\` a
\`hand_off\`. The addressee's script names the question it answers with
\`{{this.id}}\`.

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: ask
      with:
        default: red
        question:
          hand_off:
            brief: Which colour should the banner be?
            script:
              turn:
                - call:
                    tool: answer
                    with:
                      question_id: "{{this.id}}"
                      text:
                        hand_off:
                          brief: blue
                          script:
                            turn:
                              - say: The owner chose blue.
                - say: Answered.
  - say: Asked my owner.
\`\`\`

An asker that calls \`wait_for_answers\` instead keeps its turn, and the answer is that
call's result: \`{{turn[N].result.answers.0.text}}\`.

## Example: reading an image

Attach an image, then have the agent read it. The reader's script travels in
\`image_read\`'s \`prompt\` as a \`hand_off\`. The reader receives the image for real, but its
answer is whatever its script says; a script never "sees" pixels. Replace \`IMAGE_ID\` with
the id the upload returned.

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: image_read
      with:
        images: ["image://IMAGE_ID"]
        prompt:
          hand_off:
            brief: What is the dominant colour of this image?
            script:
              turn:
                - say: red
  - say: "The image is {{turn[0].result.answer}}"
\`\`\`

## Example: exploring a workspace

\`explore\` takes the topics you choose and runs one explorer per topic. Each topic's
text carries its explorer's script; \`background: false\` waits for every topic and
returns their findings together.

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: explore
      with:
        background: false
        topics:
          - topic:
              hand_off:
                brief: List the top-level folders.
                script:
                  turn:
                    - call:
                        tool: file_list
                        with:
                          path: .
                    - say: The workspace has source and docs folders.
  - say: "Explored: {{turn[0].result}}"
\`\`\`

## Example: setting up a workspace with instructions

\`workspace_init\` (the **Setup** entry, or \`/init\`) takes optional \`instructions\`: what the
user wants the initializer to focus on, up to 64 KiB. They go into the initializer's
kickoff message, so a script in them drives the kickoff. Typing \`#workspace_init\`, the
request and a script makes the chat agent pass them on:

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: workspace_init
      with:
        instructions:
          hand_off:
            brief: Set up this workspace and focus on the Claude rules.
            script:
              turn:
                - call:
                    tool: file_list
                    with:
                      path: .claude
                - say: "Found a Claude layout under .claude/."
  - say: Setup started.
\`\`\`

Through the SDK, \`chat.invoke('workspace_init', { instructions })\` passes them directly.

## Decision names

Some turns start the platform's own decision agents: choosing a process, planning a
graph, picking a step's persona, judging a goal. Each reads only the entry under its
name in \`decisions\`, never your \`turn\`. An entry is a script (usually one \`call\` to the
decision's tool) or a list of scripts; with a list, the agent's first decision reads
entry 0, its second entry 1, and so on, and past the end there is no script. A decision
agent with no script addressed to it fails the turn visibly, because its default reply
is not the one tool result it must return. An unknown name is an error.

| Name | Read by | Its tool | When it reads | Occurrence |
|------|---------|----------|---------------|------------|
| \`process_selector\` | the automatic process selector | \`automatic_process_select\` (\`process_id\`, \`reason\`, \`user_intent\`, optional \`intent_detail\`) | each turn of a chat in automatic process mode | one per turn |
| \`process_planner\` | the goal-driven planner | \`internal_graph_derive\` (\`goal\`, \`steps\`) | each pass of a process with a dynamic step, reading that pass's goal | one script per goal |
| \`step_persona\` | the step-persona selector | \`internal_step_persona_select\` (\`persona_name\`) | a process step in automatic persona mode | one per step |
| \`goal_evaluator\` | the task check | \`internal_goal_evaluate\` (\`met\`, \`critique\`) | each time the main agent stops with Keep working on, reading the user's latest message | entry *n* for the *n*+1th check since that message |
| \`feed_announce\` | the Live Feed composer | \`feed_announce_work\` (\`body\`, up to 280 characters) | when a goal, initiative, project or ticket starts or ships, reading that item's description | 0 when it starts, 1 when it ships |
| \`manager_advance\` | the goal's manager | any of its tools, usually \`assign_project\` | after a project is verified, reading that project's description | one script |
| \`manager_verify\` | the goal's manager | \`set_project_verification\` | when a project is waiting on verification, reading that project's description | entry *n* after *n* earlier \`not_ready\` returns |

When the task check finds work remaining, its \`critique\` becomes a "Task check" message
to the agent, and that message starts the agent's next turn: give the critique a
\`hand_off\`, and that turn follows its script from the first step.

## Example: automatic process selection, including a stall

The selector picks the checklist process and says why; the chosen process then runs
the turn. Replace the id with your process's.

\`\`\`shogunchop-test/v2
turn:
  - say: "1. Issue a new key. 2. Deploy it. 3. Revoke the old one."
decisions:
  process_selector:
    turn:
      - call:
          tool: automatic_process_select
          with:
            process_id: 6b1f0e2a-0000-4000-8000-000000000001
            reason: The user asked for an ordered checklist.
            user_intent: artifact
\`\`\`

A selector that never answers is a paced step: after the selection deadline (150 s)
the turn fails with "choosing a process took too long", and a reload in the meantime
shows the turn as **Choosing a process**.

\`\`\`shogunchop-test/v2
turn:
  - say: never sent
decisions:
  process_selector:
    turn:
      - call:
          tool: automatic_process_select
          with:
            process_id: 6b1f0e2a-0000-4000-8000-000000000001
            reason: never sent
            user_intent: artifact
        pace:
          first_content_ms: 600000
          chunk_gap_ms: 0
\`\`\`

## Example: a goal-driven turn

On the Goal-Driven process the planner derives one step, whose own script answers, and
the turn replies. \`agent\` names one of your personas. A process run is one pass; the
goal is checked afterwards by the task check, not inside the run.

\`\`\`shogunchop-test/v2
turn:
  - say: "Unit and integration tests."
decisions:
  process_planner:
    turn:
      - call:
          tool: internal_graph_derive
          with:
            goal: Name two kinds of test
            steps:
              - id: kinds
                action: research
                agent: Researcher
                depends_on: []
                task:
                  hand_off:
                    brief: List the kinds.
                    script:
                      turn:
                        - say: "Unit and integration."
\`\`\`

## Example: Keep working and the task check

With Keep working on, the agent records a task and stops. The first check finds the task
unfinished; its critique hands the next turn a script, which finishes the task. The
second check accepts the work and nothing more runs.

\`\`\`shogunchop-test/v2
turn:
  - call:
      tool: task_add
      with:
        title: Write the summary
  - say: Started the summary.
decisions:
  goal_evaluator:
    - turn:
        - call:
            tool: internal_goal_evaluate
            with:
              met: false
              critique:
                hand_off:
                  brief: "T1 is not done: the summary is not written yet."
                  script:
                    turn:
                      - call:
                          tool: task_update
                          with:
                            id: T1
                            status: done
                      - say: The summary is written.
    - turn:
        - call:
            tool: internal_goal_evaluate
            with:
              met: true
              critique: Every task is done.
\`\`\`

## Example: Shogun builds a team and runs its goal

One message to Shogun carries the only script. Shogun creates two personas on your
default model, a team on an existing workspace, a manager and a writer, and a goal whose objective hands the
manager its script. From there every agent reads its own part: the manager decomposes
the goal into two ordered projects, each project's owner plans a ticket, each ticket's
worker writes a file and finishes, the manager verifies each project, and verifying the
first project assigns the second. Named scripts keep the tree readable; \`outer\` carries
the writer's seat id from Shogun's turn into the manager's script, and \`this\` carries
each work item's own ids. Replace \`CEO_POSITION_ID\` with your CEO seat's id, and \`WORKSPACE_ID\` with the workspace the team works in.

\`\`\`shogunchop-test/v2
instructions: You are running a scripted test. Do exactly what each step says, in order.
turn:
  - call:
      tool: model_default
  - call:
      tool: platform_call
      with:
        resource: persona
        method: getParamSchema
        arguments:
          providerType: "{{turn[0].result.provider_type}}"
          modelName: "{{turn[0].result.model_name}}"
  - call:
      tool: platform_call
      with:
        resource: persona
        method: createPersonaWithConfig
        arguments:
          name: Brief Manager
          description: Manages the launch brief.
          fingerprint: "{{turn[1].result.result.fingerprint}}"
          agent_config:
            provider_type: "{{turn[0].result.provider_type}}"
            model_name: "{{turn[0].result.model_name}}"
            system_prompt: You manage the launch brief team.
            completion_params: {}
            agent_process_id: b0b0b0b0-0001-4000-8000-000000000001
            automatic_process_selection: false
            automatic_process_ids: []
  - call:
      tool: platform_call
      with:
        resource: persona
        method: createPersonaWithConfig
        arguments:
          name: Brief Writer
          description: Writes and edits the launch brief.
          fingerprint: "{{turn[1].result.result.fingerprint}}"
          agent_config:
            provider_type: "{{turn[0].result.provider_type}}"
            model_name: "{{turn[0].result.model_name}}"
            system_prompt: You write the launch brief.
            completion_params: {}
            agent_process_id: b0b0b0b0-0001-4000-8000-000000000001
            automatic_process_selection: false
            automatic_process_ids: []
  - call:
      tool: platform_call
      with:
        resource: workManagement
        method: createTeam
        arguments:
          name: Launch Brief
          identifier_prefix: LNCH
          workspace_id: WORKSPACE_ID
  - call:
      tool: platform_call
      with:
        resource: orgChart
        method: createPosition
        arguments:
          title: Brief Manager
          role: manager
          parent_id: CEO_POSITION_ID
          team_id: "{{turn[4].result.result.id}}"
          persona_id: "{{turn[2].result.result.persona_id}}"
          agent_process_id: b0b0b0b0-0001-4000-8000-000000000001
  - call:
      tool: platform_call
      with:
        resource: orgChart
        method: createPosition
        arguments:
          title: Brief Writer
          role: ic
          parent_id: "{{turn[5].result.result.id}}"
          team_id: "{{turn[4].result.result.id}}"
          persona_id: "{{turn[3].result.result.persona_id}}"
          agent_process_id: b0b0b0b0-0001-4000-8000-000000000001
  - call:
      tool: platform_call
      with:
        resource: workManagement
        method: createGoal
        arguments:
          team_id: "{{turn[4].result.result.id}}"
          schedule_kind: immediate
          objective:
            hand_off:
              brief: Write the launch brief, then edit it.
              script: goal
  - say: The team is built and its goal is running.
scripts:
  goal:
    turn:
      - call:
          tool: create_initiative
          with:
            team_id: "{{this.team_id}}"
            goal_id: "{{this.id}}"
            owner_position_id: "{{this.manager_position_id}}"
            name: Launch brief
            state: active
            description:
              hand_off:
                brief: The launch brief stream.
                script:
                  decisions:
                    feed_announce:
                      - turn:
                          - call:
                              tool: feed_announce_work
                              with:
                                body: The launch brief is under way.
                      - turn:
                          - call:
                              tool: feed_announce_work
                              with:
                                body: The launch brief is done.
      - call:
          tool: create_project
          with:
            team_id: "{{this.team_id}}"
            initiative_id: "{{turn[0].result.id}}"
            name: Edit the brief
            description:
              hand_off:
                brief: Edit the draft brief.
                script: edit_project
      - call:
          tool: create_project
          with:
            team_id: "{{this.team_id}}"
            initiative_id: "{{turn[0].result.id}}"
            name: Write the brief
            description:
              hand_off:
                brief: Write the draft brief.
                script: write_project
      - call:
          - tool: assign_project
            with:
              project_id: "{{turn[2].result.id}}"
              assignee_position_id: "{{outer.turn[6].result.result.id}}"
          - tool: add_work_ordering
            with:
              goal_id: "{{this.id}}"
              node_kind: project
              blocked_node_id: "{{turn[1].result.id}}"
              blocking_node_id: "{{turn[2].result.id}}"
      - say: Two projects, writing before editing.
  write_project:
    turn:
      - call:
          tool: create_ticket
          with:
            team_id: "{{this.team_id}}"
            project_id: "{{this.id}}"
            title: Draft the brief
            description:
              hand_off:
                brief: Draft the brief.
                script: draft_ticket
      - say: Planned the draft.
    decisions:
      feed_announce:
        - turn:
            - call:
                tool: feed_announce_work
                with:
                  body: Drafting the launch brief.
        - turn:
            - call:
                tool: feed_announce_work
                with:
                  body: The launch brief draft is done.
      manager_verify:
        turn:
          - call:
              tool: set_project_verification
              with:
                goal_id: "{{this.goal_id}}"
                project_id: "{{this.id}}"
                verification_state: accepted
          - say: The draft is accepted.
      manager_advance:
        turn:
          - call:
              tool: assign_project
              with:
                project_id: "{{outer.turn[1].result.id}}"
                assignee_position_id: "{{this.assignee_position_id}}"
          - say: Editing is assigned.
  edit_project:
    turn:
      - call:
          tool: create_ticket
          with:
            team_id: "{{this.team_id}}"
            project_id: "{{this.id}}"
            title: Edit the brief
            description:
              hand_off:
                brief: Edit the brief.
                script: edit_ticket
      - say: Planned the edit.
    decisions:
      feed_announce:
        - turn:
            - call:
                tool: feed_announce_work
                with:
                  body: Editing the launch brief.
        - turn:
            - call:
                tool: feed_announce_work
                with:
                  body: The launch brief is edited.
      manager_verify:
        turn:
          - call:
              tool: set_project_verification
              with:
                goal_id: "{{this.goal_id}}"
                project_id: "{{this.id}}"
                verification_state: accepted
          - say: The edit is accepted.
      manager_advance:
        turn:
          - say: Nothing is left to assign.
  draft_ticket:
    turn:
      - call:
          tool: file_write
          with:
            path: launch-brief/draft.md
            content: "# Launch brief\\n\\nThe draft.\\n"
      - call:
          tool: transition_state
          with:
            ticket_id: "{{this.id}}"
            target_state: done
      - say: Drafted.
  edit_ticket:
    turn:
      - call:
          tool: file_write
          with:
            path: launch-brief/edited.md
            content: "# Launch brief\\n\\nThe edited brief.\\n"
      - call:
          tool: transition_state
          with:
            ticket_id: "{{this.id}}"
            target_state: done
      - say: Edited.
\`\`\`

## One script per turn

A script drives only the turn whose own message carries it. A later message without a
script gets the default reply, even if the earlier script had steps left over. Give
every turn that needs scripted output its own script. Corrections and nudges the
platform adds in the middle of a turn keep the turn on its script.

## When things go wrong

| What happened | What you see |
|---------------|--------------|
| The message has no script | The default reply: "Test provider default reply: no script was found in this conversation, so nothing was scripted." |
| The script is malformed (bad YAML, unknown field, wrong tag) | The turn fails with "test provider script is invalid: …" naming the problem. |
| A reference can't be resolved (missing field, failed tool call) | The turn fails naming the reference and why. |
| An \`expect\` doesn't hold | The turn fails naming the step, the path, the expected and the actual value. |
| The turn takes more model calls than the script has steps | The extra calls get the default reply. |
| The script calls a tool the agent isn't offered | The call fails the way a real model's would, and the agent sees the correction. |

## Building scripts with the SDK

\`testScript(document, {format})\` from \`@localagent/sdk\` builds the envelope, writes
the default \`instructions\`, and emits YAML (or JSON with \`format: 'json'\`).
\`handOff(script, brief)\` builds a \`hand_off\` value. Its types list every field, step
and matcher above.
`;

export const GUIDES: Guide[] = [
  {
    slug: 'getting-started',
    title: 'Getting Started',
    tags: ['setup', 'providers', 'models', 'personas'],
    body: GETTING_STARTED_BODY,
  },
  {
    slug: 'key-concepts',
    title: 'Key Concepts',
    tags: ['concepts', 'personas', 'agent-processes', 'workspaces', 'context'],
    body: KEY_CONCEPTS_BODY,
  },
  {
    slug: 'test-scripts',
    title: 'Test Scripts',
    tags: ['testing', 'agents', 'mcp'],
    body: TEST_SCRIPTS_BODY,
  },
];
